import { PropertyFilterProcessingNode } from './PropertyFilterProcessingNode';
import { Vector3 } from '../../../utils';
/**
 * Kalman Filter processing node
 * @category Processing node
 */
export class KalmanFilterNode extends PropertyFilterProcessingNode {
  constructor(propertySelector, propertyModifier, options) {
    super(propertySelector, propertyModifier, options);
  }
  initFilter(object, value, options) {
    return new Promise(resolve => {
      Object.keys(options).forEach(key => {
        if (typeof options[key] === 'number' && ['R', 'Q', 'A', 'B', 'C'].includes(key)) {
          options[key] = new Vector3(options[key], 0, 0);
        }
      });
      resolve(Object.assign({
        x: undefined,
        cov: NaN
      }, options));
    });
  }
  filter(object, value, filter) {
    return new Promise(resolve => {
      const kf = new KalmanFilter(filter.R, filter.Q, filter.A, filter.B, filter.C, filter.x, filter.cov);
      const numeric = typeof value === 'number';
      if (numeric) {
        kf.filter(new Vector3(value, 0, 0));
      } else {
        kf.filter(value);
      }
      // Save the node data
      filter.x = kf.measurement;
      filter.cov = kf.covariance;
      if (numeric) {
        resolve(kf.measurement.x);
      } else {
        resolve(kf.measurement);
      }
    });
  }
}
/**
 * Basic Kalman Filter
 * @author Wouter Bulten
 * @see {@link http://github.com/wouterbulten/kalmanjs}
 * @copyright Copyright 2015-2018 Wouter Bulten
 * @license MIT
 */
export class KalmanFilter {
  constructor(R, Q, A, B, C, x, cov) {
    this._R = R;
    this._Q = Q;
    this._A = A;
    this._B = B;
    this._C = C;
    this._x = x;
    this._cov = cov;
  }
  /**
   * Filter a new value
   * @param  {Vector3} z Measurement
   * @param  {Vector3} u Control
   * @returns {Vector3} Filtered value
   */
  filter(z, u) {
    if (this._x === undefined) {
      const ct = new Vector3(1, 1, 1).divide(this._C);
      this._x = ct.clone().multiply(z);
      this._cov = ct.clone().multiply(this._Q).multiply(ct);
    } else {
      // Compute prediction
      const predX = this.predict(u);
      const predCov = this.uncertainty();
      // Kalman gain
      const K = predCov.clone().multiply(this._C).multiply(new Vector3(1, 1, 1).divide(this._C.clone().multiply(predCov).multiply(this._C).add(this._Q)));
      // Correction
      this._x = predX.clone().add(K.clone().multiply(z.clone().sub(this._C.clone().multiply(predX))));
      this._cov = predCov.clone().sub(K.clone().multiply(this._C).multiply(predCov));
    }
    return this._x;
  }
  /**
   * Predict next value
   * @param  {Vector3} [u] Control
   * @returns {Vector3} Predicted value
   */
  predict(u) {
    return this._A.clone().multiply(this._x).add(u === undefined ? new Vector3() : this._B.clone().multiply(u));
  }
  /**
   * Return uncertainty of filter
   * @returns {number} Uncertainty
   */
  uncertainty() {
    return this._A.clone().multiply(this._cov).multiply(this._A).add(this._R);
  }
  /**
   * Return the last filtered measurement
   * @returns {Vector3} Last measurement
   */
  get measurement() {
    return this._x;
  }
  /**
   * Get covariance
   * @returns {Vector3} covariance vector
   */
  get covariance() {
    return this._cov;
  }
}